// © 2026 Buddy Media LLC. All rights reserved.
import { useEffect, useState } from "react";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { ApiError, apiFetch } from "@/lib/api";

// /admin/operations/cluster-topology — canonical "who works in this
// MCP cluster" surface (T-080). Renders the contents of
// docs/CLUSTER_TOPOLOGY.md as a read-only view inside the admin UI
// so operators can reference cluster membership without leaving the
// app.
//
// Distinct from /admin/dashboard/cluster-topology, which is a LIVE
// status snapshot (heartbeat-based health bands per node, pulled
// from q_stream). This page is the canonical reference doc — what
// the cluster IS, not how it's behaving right now.
//
// Frontend-defined contract; backend admin handler is a follow-up:
//
//   GET /api/admin/operations/cluster-topology
//     -> { content: string, source: string, last_updated_at: string }
//     permission: ops.cluster_topology.view (or any admin)
//
// Backend implementation choice (out of frontend scope):
//   (a) Read docs/CLUSTER_TOPOLOGY.md from the repo (mounted into
//       the backend container or via a build-time embed)
//   (b) Proxy to the MCP server's get_cluster_topology() tool
//       (the bible mentions this as the source of truth)
// The contract is identical from the frontend's perspective.

type ClusterTopologyDoc = {
  content: string;
  source: string;
  last_updated_at: string; // ISO8601
};

type State =
  | { kind: "loading" }
  | { kind: "ready"; doc: ClusterTopologyDoc }
  | { kind: "not-implemented" }
  | { kind: "error"; message: string };

const NEW_REPO_DOCS_URL =
  "https://github.com/UpgradedMR/dev-buddywatch-docs/blob/main/CLUSTER_TOPOLOGY.md";

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.data as { detail?: string } | undefined)?.detail;
    return `${fallback} (HTTP ${err.status})${detail ? `: ${detail}` : ""}`;
  }
  return fallback;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 16).replace("T", " ");
}

export function OperationsClusterTopologyPage() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    apiFetch<ClusterTopologyDoc>("/api/admin/operations/cluster-topology")
      .then((doc) => {
        if (!cancelled) setState({ kind: "ready", doc });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: "not-implemented" });
        } else {
          setState({
            kind: "error",
            message: describeError(err, "Topology load failed"),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return (
    <AdminPageShell
      actions={
        <div className="flex items-center gap-2">
          <a
            href={NEW_REPO_DOCS_URL}
            target="_blank"
            rel="noreferrer"
            className="rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-500"
          >
            View on GitHub →
          </a>
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            className="rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:border-slate-500"
          >
            Refresh
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <AdminCard className="space-y-2">
          <h2 className="text-base font-semibold text-white">
            Cluster topology — canonical reference
          </h2>
          <p className="text-sm text-slate-400">
            The single source of truth for cluster membership: hostname,
            IP, repo path, Anthropic account, and role for every active
            node. Every chat / worker session loads this at startup
            (cardinal rule: read-cluster-topology-on-session-start).
          </p>
          <p className="text-xs italic text-slate-500">
            Looking for live health (heartbeats, deploy status)? See{" "}
            <a
              href="/admin/dashboard/cluster-topology"
              className="text-pink-400 hover:underline"
            >
              /admin/dashboard/cluster-topology
            </a>
            .
          </p>
        </AdminCard>

        {state.kind === "loading" && (
          <AdminCard>
            <p className="text-sm text-slate-400">Loading topology…</p>
          </AdminCard>
        )}

        {state.kind === "not-implemented" && (
          <AdminCard className="border-slate-700">
            <p className="text-sm text-slate-300">
              Backend endpoint for the canonical topology view is not
              yet shipped.
            </p>
            <p className="mt-2 text-xs text-slate-400">
              Contract this page is built against:
            </p>
            <pre className="mt-1 overflow-x-auto rounded-md border border-slate-800 bg-slate-950/60 p-2 text-[11px] text-slate-400">{`GET /api/admin/operations/cluster-topology
  -> { content: string,           // raw markdown
       source: string,            // e.g. "docs/CLUSTER_TOPOLOGY.md"
       last_updated_at: string }  // ISO8601 (file mtime or git log)`}</pre>
            <p className="mt-2 text-[11px] italic text-slate-500">
              Backend can read the file from the docs repo or proxy
              the MCP get_cluster_topology() tool — the contract is
              identical from this page's perspective. In the meantime
              the GitHub link above opens the canonical doc directly.
            </p>
          </AdminCard>
        )}

        {state.kind === "error" && (
          <AdminCard className="border-rose-900/40 bg-rose-950/20">
            <p className="text-sm text-rose-200">{state.message}</p>
          </AdminCard>
        )}

        {state.kind === "ready" && (
          <AdminCard className="space-y-3">
            <div className="flex flex-wrap items-baseline gap-3 text-xs text-slate-500">
              <span>
                Source:{" "}
                <code className="text-slate-400">{state.doc.source}</code>
              </span>
              <span>
                Updated{" "}
                <span className="font-mono text-slate-400">
                  {fmtDateTime(state.doc.last_updated_at)}
                </span>
              </span>
            </div>
            <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-slate-800/60 bg-slate-950/60 p-4 font-mono text-xs text-slate-200">
              {state.doc.content}
            </pre>
          </AdminCard>
        )}
      </div>
    </AdminPageShell>
  );
}
